import cn from 'clsx'
import Pane from 'components/Common/Pane'
import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import OfferGallery from '../OfferGallery'
import PackageOptions from './AlwaysOnRoomOptions'

import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import {
  fetchAvailableRatesForEachRoom,
  fetchAvailableRatesForOffer,
  fetchOfferFlightPrice,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import * as Analytics from 'analytics/analytics'
import { calendarAction, offerPageWatchVideo } from 'analytics/eventDefinitions'
import { offerEvent, offerPageHotLeadEvent } from 'analytics/snowplow/events'
import { fireInteractionEvent, fireNonInteractionEvent } from 'api/googleTagManager'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import Heading from 'components/Luxkit/Typography/Heading'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import MultipleRoomsBookingContent from 'components/OfferPage/MultipleRoomsBooking/MultipleRoomsBookingContent'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useDispatchPrecacheLere from 'components/Recommendations/hooks/useDispatchPrecacheLere'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { RouterLocation, replace } from 'connected-react-router'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL, GA_ACTION_TO_SNOWPLOW_MAP } from 'constants/analytics'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_ALWAYS_ON_ANALYTICS } from 'constants/offer'
import { urlHashDatePicker } from 'constants/url'
import AnalyticsComponentContext, { AnalyticsComponent } from 'contexts/Analytics/analyticsComponentContext'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import GeoContext from 'contexts/geoContext'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import noop from 'lib/noop'
import { generateRoomOccupants, sumUpOccupancies } from 'lib/occupancyUtils'
import offerPageURL from 'lib/offerPageURL'
import { hasOfferExpired } from 'lib/offerUtils'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely'
import { removeSearchParamsFromURL } from 'lib/searchUtils'
import {
  multiRoomEnabled,
} from 'selectors/featuresSelectors'
import { getInitialLeOfferPageState } from 'selectors/offerPageSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import { useSyncHotelTripItem } from 'tripPlanner/hooks/bookmarks/useSyncHotelTripItem'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import OfferUpgradeModalContext from '../OfferUpgradeModalContext'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import OfferIntro from './AlwaysOnIntro'
import AlwaysOnSidebarFilters from './AlwaysOnSidebarFilters'
import AlwaysOnStickyHeader from './AlwaysOnStickyHeader'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import OfferYmalStickyFooter from 'components/Recommendations/YouMayAlsoLike/Offer/OfferYmalStickyFooter'
import { useInView } from 'react-intersection-observer'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

interface Props {
  offer: App.Offer;
  defaults: Partial<OfferPageState>;
  routerLocation: RouterLocation<unknown>;
  airports?: Array<App.Airport>;
  originAirports?: Array<App.Airport>;
  isMultiRoomEnabled: boolean;
  lereOfferExpired: boolean;
  regionCode: string;
  shouldUseLereStickyFooter: boolean;
}

const fallbackRooms = [generateRoomOccupants()]

function AlwaysOnContent(props: Props) {
  const {
    offer,
    defaults,
    routerLocation,
    airports,
    originAirports,
    isMultiRoomEnabled,
    lereOfferExpired,
    regionCode,
    shouldUseLereStickyFooter,
  } = props

  const {
    id,
    packages,
  } = offer

  const [viewedDates, setViewedDates] = useState(!!defaults.checkInDate)
  const [state, stateDispatch, stateSnapshotRef] = useOfferPageReducer({
    ...defaults,
    duration: defaults.duration ?? offer.lowestPricePackage?.duration,
    offer,
    flights: offer.bundledWithFlightsOnly,
  })
  const dispatch = useAppDispatch()

  const shouldShowLoggedOutPrice = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.searchShowLoggedOutPrices)

  const {
    rooms = fallbackRooms,
    duration,
    activeFilter,
    package: selectedPackage,
    checkInDate,
    airport,
  } = state

  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousState = usePrevious<OfferPageState>(state, {})
  const packageOptionsAnchor = useRef<HTMLDivElement>()
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants>>()
  const lastFetchedAirport = useRef<App.Airport>()

  const onUserAnalyticsAction = useCallback((action: string, label?: string) => {
    const event = calendarAction(OFFER_TYPE_ALWAYS_ON_ANALYTICS, action, label)
    fireInteractionEvent(event)

    const snowplowEvent = GA_ACTION_TO_SNOWPLOW_MAP[event.action]
    if (snowplowEvent) {
      const event = offerEvent(snowplowEvent, stateSnapshotRef.current)
      if (event) Analytics.track('offer-event', event)
    }
  }, [stateSnapshotRef])

  useSyncHotelTripItem(state, offer)

  const isoCheckIn = state?.checkInDate?.format(ISO_DATE_FORMAT)
  const isoCheckOut = state?.checkOutDate?.format(ISO_DATE_FORMAT)

  const canBookMultiRoomTypes = isMultiRoomEnabled && rooms.length > 1

  // Need to pass the context state above the tree
  useEffect(() => {
    if (state.offer) {
      dispatch({
        type: SET_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer.id,
          checkIn: isoCheckIn,
          checkOut: isoCheckOut,
          duration,
        },
      })
    }
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: state.offer.id,
        },
      })
    }
  }, [dispatch, state.offer, isoCheckIn, isoCheckOut, duration])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
    previousState.rooms !== rooms ||
    previousState.checkInDate !== checkInDate
  }, [state.rooms, state.checkInDate])

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const fetchCalendarData = useCallback(() => {
    if (
      !offer.packages.length ||
      rooms.some(room => room.childrenAge?.some(age => age === -1))
    ) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }
    if (canBookMultiRoomTypes) {
      dispatch(fetchCalendarsByOccupancy(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    } else {
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: [selectedPackage ?? offer.lowestPricePackage],
      }))
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    }

    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport
  }, [airport, canBookMultiRoomTypes, dispatch, duration, offer, rooms, selectedPackage])

  const scrollToPackageOptions = useCallback(() => {
    packageOptionsAnchor.current?.scrollIntoView()
  }, [])

  const showLightbox = useContext(LightBoxContext)
  const onViewPackageOptions = useCallback(() => {
    scrollToPackageOptions()
    Analytics.track('offer-event', offerEvent('VIEW_PACKAGE_OPTIONS_PRESSED', stateSnapshotRef.current))
  }, [scrollToPackageOptions, stateSnapshotRef])

  const watchVideo = useCallback(() => {
    fireInteractionEvent(offerPageWatchVideo())
    showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)
  }, [offer.vimeoVideoId, showLightbox])

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    const hasDate = !!checkInDate
    const hasInvalidChildren = rooms.some(room => room.childrenAge?.some(age => age === -1))

    if (previousFilter === 'capacity') {
      if (!hasRooms) {
        // user looked at capacity, didn't change anything and closed it
        // we consider this them applying the default rooms, so dispatch a proper update
        stateDispatch({
          type: OfferPageActions.setRooms,
          rooms: [generateRoomOccupants()],
        })

        if (!activeFilter) {
          scrollToPackageOptions()
          scrollAfterFilterClose.current = false
        }
      }

      if (!activeFilter && !hasDate && !hasInvalidChildren) {
        stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
        fireInteractionEvent(calendarAction(offer.analyticsType, CALENDAR_GA_EVENT_ACTION.OpenFilter, CALENDAR_GA_EVENT_LABEL.PackagePaneHeader))
      }
    }

    if (activeFilter === 'checkin' && hasInvalidChildren) {
      // trying to open the calendar with invalid children ages, means we can't fetch calendar
      // direct them to the capacity instead to fill it in
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (previousFilter === 'checkin' && !activeFilter && hasDate && !hasRooms) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (activeFilter) {
      scrollToPackageOptions()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (state.room) {
          window.location.hash = state.room.id
        } else {
          scrollToPackageOptions()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else if (viewedDates && (lastFetchedAirport.current !== airport || lastFetchedRooms.current !== rooms)) {
      // Filter has just closed, so we're applying it now
      // rooms has changed, all loaded calendars are no longer valid as we load calendars for specific
      // rooms (availablity), so clear them and refetch
      dispatch(clearBookingCalendar())
      fetchCalendarData()
    } else if (canBookMultiRoomTypes && lastFetchedRooms.current !== rooms) {
      // if multiple room booking is available and we changed the rooms,
      // don't clear the previous data, just update for the new changed
      // rooms (is filtered inside fetchCalendarsByOccupancy action)
      fetchCalendarData()
    }
  }, [activeFilter, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    if (routerLocation.query.roomTypeId && routerLocation.query.roomRateId) {
      const roomOption = { roomTypeId: routerLocation.query.roomTypeId, roomRateId: routerLocation.query.roomRateId }
      stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
    }
  }, [routerLocation.query.roomTypeId, routerLocation.query.roomRateId, stateDispatch])

  const hasSelectedPackageWithDifferentDuration = selectedPackage && selectedPackage?.duration !== duration
  useEffect(() => {
    const hasDate = !!checkInDate
    const useDynamicCancellationPolicies = offer.property?.useDynamicCancellationPolicies ?? false
    if (hasDate && (!activeFilter || hasSelectedPackageWithDifferentDuration) && useDynamicCancellationPolicies) {
      if (canBookMultiRoomTypes) {
        dispatch(fetchAvailableRatesForEachRoom(
          offer,
          {
            occupants: rooms,
            checkIn: checkInDate.format(ISO_DATE_FORMAT),
            checkOut: checkInDate.clone().add(duration, 'days').format(ISO_DATE_FORMAT),
            flightOrigin: airport?.code,
          },
        ))
      } else {
        dispatch(fetchAvailableRatesForOffer(
          offer,
          {
            occupants: rooms,
            checkIn: checkInDate.format(ISO_DATE_FORMAT),
            checkOut: checkInDate.clone().add(duration, 'days').format(ISO_DATE_FORMAT),
            flightOrigin: airport?.code,
          },
        ))
      }
    }
  }, [duration, activeFilter, checkInDate, offer, hasSelectedPackageWithDifferentDuration, dispatch, rooms, airport, canBookMultiRoomTypes])

  const geoState = useContext(GeoContext)
  const hotLead = useSaveHotLead({
    offerId: id,
    offerType: offer.type,
    checkInDate,
    duration,
    occupancy: rooms,
    defaultOccupancy: fallbackRooms,
  })

  useEffect(() => {
    if (hotLead && hotLead.checkIn && hotLead.checkOut) {
      const event = offerPageHotLeadEvent(offer, hotLead, geoState)
      if (event) Analytics.track('offer-page-hot-lead-event', event)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotLead])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      scrollToPackageOptions()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (routerLocation.search) {
      const newSearch = removeSearchParamsFromURL(routerLocation.search)
      dispatch(replace({ ...routerLocation, search: newSearch }))
    }

    if (routerLocation.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      fireNonInteractionEvent(calendarAction(offer.analyticsType, CALENDAR_GA_EVENT_ACTION.OpenFilter, CALENDAR_GA_EVENT_LABEL.RouterLocation))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return packages.flatMap((p) => p.roomRate.capacities)
  }, [packages])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy
  const availableForPurchase = useIsAvailableForPurchase(state)

  const shouldTriggerPrecache = !!(availableForPurchase && state.offer)

  useDispatchPrecacheLere(shouldTriggerPrecache, state.offer, state.duration, state.rooms)

  // ------------- LERE sticky footer -------------------
  const recommendationFilters = useMemo(() => ({
    checkIn: isoCheckIn,
    checkOut: isoCheckOut,
    rooms,
  }), [isoCheckIn, isoCheckOut, rooms])

  const ymalStickyFooterHideTrigger = useInView({ triggerOnce: false })
  const ymalStickyFooterShowTrigger = useInView({ triggerOnce: true })

  const canShowYmalFooter = ymalStickyFooterShowTrigger.inView && !ymalStickyFooterHideTrigger.inView
  // ------------- END of LERE sticky footer -------------------

  if (offer.altLink) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  return (
    <UserAnalyticsActionContext.Provider value={onUserAnalyticsAction}>
      <OfferPageStateProvider state={state} stateSnapshotRef={stateSnapshotRef} dispatch={stateDispatch}>
        <ImmersiveSaveButtonPortal>
          <BookmarkButton offer={offer} />
        </ImmersiveSaveButtonPortal>
        <OfferUpgradeModalContext>
          <RelativeBlock onSubmit={(e) => e.preventDefault()}>
            <div>
              <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
              <AlwaysOnStickyHeader
                offer={offer}
                onViewPackageOptions={onViewPackageOptions}
              />
              {lereOfferExpired && (
                <div>
                  <ExpiredDealsLereRecommendations
                    hideOffers={!!activeFilter}
                  />
                </div>
              )}
              {availableForPurchase && (
                <WalledContent enforced={shouldShowLoggedOutPrice ? false : offer.walledGarden}>
                  <Breakpoint max="xs">
                    <AlwaysOnSidebarFilters
                      onViewPackageOptions={onViewPackageOptions}
                      capacities={occupancyLimits}
                    />
                  </Breakpoint>
                </WalledContent>
              )}
              <span ref={ymalStickyFooterHideTrigger.ref} />
              <OfferGallery offer={offer} />
              <Pane>
                <OfferIntro
                  offer={offer}
                  onWatchVideo={watchVideo}
                  onViewPackageOptions={onViewPackageOptions}
                />
              </Pane>
            </div>
            <ScrollAnchor ref={packageOptionsAnchor} />
            <span ref={ymalStickyFooterShowTrigger.ref} />
            <PackageOptionsPane type="light" id="Package-Options">
              <LayoutContainer>
                <VerticalSpacer gap={32}>
                  <VerticalSpacer gap={16}>
                    <Heading variant="heading2" format="titlecase" as="h2" align="center">
                      {`${config.brandConfig.travelSynonym} Options`}
                    </Heading>
                    {availableForPurchase && (
                      <WalledContent enforced={shouldShowLoggedOutPrice ? false : offer.walledGarden}>
                        <>
                          <CSSBreakpoint max="xs">
                            <Group direction="vertical" gap={8}>
                              <OfferCapacityWithAgesSelectBox />
                              <OfferCalendarDatePickerSelectBox onClickLabel={CALENDAR_GA_EVENT_LABEL.PackagePaneHeader}/>
                              {showFlightInformation && <OfferAirportSelectBox />}
                            </Group>
                          </CSSBreakpoint>
                          <CSSBreakpoint min="sm">
                            <DesktopFilterBlock direction="horizontal" gap={8} horizontalAlign="center" className={cn({ open: !!activeFilter })}>
                              <OfferCapacityWithAgesSelect capacities={occupancyLimits} />
                              <OfferCalendarDatePickerSelect
                                onClickLabel={CALENDAR_GA_EVENT_LABEL.PackagePaneHeader}
                              />
                              {showFlightInformation && <OfferAirportSelect />}
                            </DesktopFilterBlock>
                          </CSSBreakpoint>
                        </>
                      </WalledContent>
                    )}
                  </VerticalSpacer>
                  <VerticalSpacer gap={8}>
                    <Subtitle variant="subtitle3" align="center">
                      Select duration
                    </Subtitle>
                    <OfferDurationTabs />
                  </VerticalSpacer>
                </VerticalSpacer>
              </LayoutContainer>
            </PackageOptionsPane>
            <AnalyticsComponentContext.Provider value={AnalyticsComponent.roomOption}>
              {!canBookMultiRoomTypes && packages.length > 0 && (
                <PackageOptions
                    showFlightInformation={showFlightInformation}
                    destinationAirport={destinationAirport}
                    onViewPackageOptions={onViewPackageOptions}
                  />
              )}
            </AnalyticsComponentContext.Provider>
            {canBookMultiRoomTypes && (
              <MultipleRoomsBookingContent />
            )}
          </RelativeBlock>
        </OfferUpgradeModalContext>
        {shouldUseLereStickyFooter && (
          <ErrorBoundary fallback={null}>
            <OfferYmalStickyFooter
              seedOffer={offer}
              filters={recommendationFilters}
              show={canShowYmalFooter}
              initialShowDelayMs={2000}
              hideImmediately={!!activeFilter}
              pageSource="AlwaysOn"
            />
          </ErrorBoundary>
        )}
      </OfferPageStateProvider>
    </UserAnalyticsActionContext.Provider>
  )
}

AlwaysOnContent.defaultProps = {
  originAirports: [],
  airports: [],
  onFilterClose: noop,
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  const offer = ownProps.offer! // shouldn't be undefined
  const regionCode = state.geo.currentRegionCode

  return {
    regionCode,
    currencyCode: state.geo.currentCurrency,
    defaults: getInitialLeOfferPageState(state, offer),
    routerLocation: state.router.location,
    airports: state.geo.airports,
    originAirports: state.geo.originAirports,
    isMultiRoomEnabled: multiRoomEnabled(state, offer),
    lereOfferExpired: hasOfferExpired(offer),
  }
}

export default connect(mapStateToProps)(AlwaysOnContent)
